/**
 * /pay/<slug>/<number> — PUBLIC invoice summary + Pay button. Shows the minimal safe summary
 * (business, invoice #, amount due, status) and starts Stripe Checkout on the business's connected
 * account. The ?paid=1 query (Checkout success redirect) shows a thank-you state. No auth, no card
 * data here — Stripe owns PCI scope. NEVER imports @smartbox/*.
 */
import { ActivityIndicator, Image, StyleSheet, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { getPublicBrand, getPublicInvoice, startPublicCheckout } from '../../../../src/api/publicPay';
import { openExternalUrl } from '../../../../src/platform/linking';
import { Screen, Text, Card, Button, Badge } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';
import { extractErrorMessage } from '../../../../src/lib/format';

function formatAmount(amount: string, currency: string): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${currency} ${amount}`;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

export default function PayInvoiceScreen() {
  const { slug, number, paid } = useLocalSearchParams<{ slug: string; number: string; paid?: string }>();
  const [payError, setPayError] = useState<string | null>(null);

  const invoice = useQuery({
    queryKey: ['public-invoice', slug, number],
    queryFn: () => getPublicInvoice(slug!, number!),
    enabled: !!slug && !!number,
    retry: false,
  });

  // Brand (logo + accent) — shared cache key with the slug landing screen.
  const brand = useQuery({
    queryKey: ['public-brand', slug],
    queryFn: () => getPublicBrand(slug!),
    enabled: !!slug,
    retry: false,
    staleTime: 60_000,
  });
  const accent = brand.data?.accentColor;

  const checkout = useMutation({
    mutationFn: () => startPublicCheckout(slug!, number!),
    onSuccess: async (url) => {
      await openExternalUrl(url);
    },
    onError: (e) => setPayError(extractErrorMessage(e, 'Could not start payment. Please try again.')),
  });

  if (invoice.isLoading) {
    return (
      <Screen>
        <View style={styles.center}>
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading invoice" />
        </View>
      </Screen>
    );
  }

  if (invoice.isError || !invoice.data) {
    return (
      <Screen scroll>
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(invoice.error, 'Invoice not found. Check the number and try again.')}
          </Text>
        </Card>
      </Screen>
    );
  }

  const inv = invoice.data;
  const isPaid = inv.status === 'paid';

  return (
    <Screen scroll>
      {brand.data?.logoUrl ? (
        <Image
          source={{ uri: brand.data.logoUrl }}
          style={styles.logo}
          resizeMode="contain"
          accessibilityLabel={`${inv.businessName} logo`}
        />
      ) : null}
      <Text variant="title">{inv.businessName}</Text>

      {paid ? (
        <Card>
          <Text variant="bodyStrong" accessibilityRole="alert">
            Thank you, your payment was received.
          </Text>
        </Card>
      ) : null}

      <Card>
        <View style={styles.row}>
          <Text variant="label" color="secondary">Invoice</Text>
          <Text variant="bodyStrong">#{inv.invoiceNumber}</Text>
        </View>
        <View style={styles.row}>
          <Text variant="label" color="secondary">Amount due</Text>
          <Text variant="bodyStrong">{formatAmount(inv.amountDue, inv.currency)}</Text>
        </View>
        <View style={styles.row}>
          <Text variant="label" color="secondary">Status</Text>
          <Badge status={isPaid ? 'paid' : 'sent'} label={isPaid ? 'Paid' : 'Open'} />
        </View>
      </Card>

      {payError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{payError}</Text>
        </Card>
      ) : null}

      {!isPaid ? (
        <Button
          title={`Pay ${formatAmount(inv.amountDue, inv.currency)}`}
          size="lg"
          loading={checkout.isPending}
          disabled={checkout.isPending}
          onPress={() => {
            setPayError(null);
            checkout.mutate();
          }}
          accessibilityLabel="Pay invoice"
          style={accent ? { backgroundColor: accent } : undefined}
        />
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  center: { paddingVertical: theme.spacing.lg, alignItems: 'center' },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.sm },
  logo: { height: 56, width: 200, alignSelf: 'flex-start', marginBottom: theme.spacing.sm },
});
